import { RISK_BACKGROUND_LABELS } from './graphConstants'
import {
  getCurveGradientOpacityAtPoint,
  PATH_BOUNDS_PADDING,
  type PatternBounds,
  type ScreenPoint,
} from './RedAboveLossOfControlOverlay'
import {
  createRenderedFutureMarkerDashSegments,
  createRenderedLineSegments,
  getVisibleDataPointTimestamps,
} from './graphUtils'
import type { BackgroundPatternSettings } from './backgroundPatternStore'
import type { RiskBackgroundLabel } from './ExtrapolatedGraphLossOfControlLayer'
import type {
  ExtrapolatedGraphHoverPoint,
  ExtrapolatedGraphLineSegment,
  GraphPoint,
  GraphLineRenderBounds,
  GraphRenderSourceState,
} from './types'

type RiskBackgroundLabelSource = {
  label: string
  gradientPercent: number
}

export const getVisibleDataPointTimestampsForAxis = (renderSourceState: GraphRenderSourceState) =>
  // The leftmost / rightmost year labels (and the x-axis spacing) should
  // track the main data series, NOT the projected-loss-of-control reference
  // line. Otherwise the leftmost label sticks at projectedLossOfControl[0]'s
  // year (e.g. 2024) even when the actual capability data starts later
  // (e.g. shared starts at 2025-01-01). Mirrors the visiblePastMarkers
  // filter.
  getVisibleDataPointTimestamps({
    ...renderSourceState,
    pastMarkerSources: renderSourceState.pastMarkerSources.filter(
      source => !source.key.startsWith('projected-loss-of-control')
    ),
    futureMarkerSources: renderSourceState.futureMarkerSources.filter(
      source => !source.key.startsWith('projected-loss-of-control')
    ),
  })

export const createRenderedGraphLineSegments = ({
  lineSources,
  futureMarkerSources,
  xForTimestamp,
  yForValue,
  showYearDots,
  lineSmoothing,
  flattenSmoothedCurves = false,
  lineRenderBounds,
}: {
  lineSources: GraphRenderSourceState['lineSources']
  futureMarkerSources: GraphRenderSourceState['futureMarkerSources']
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  showYearDots: boolean
  lineSmoothing?: number
  flattenSmoothedCurves?: boolean
  lineRenderBounds?: GraphLineRenderBounds
}): ExtrapolatedGraphLineSegment[] => {
  const lineSegments = createRenderedLineSegments(
    lineSources,
    xForTimestamp,
    yForValue,
    lineSmoothing,
    flattenSmoothedCurves,
    lineRenderBounds,
  )
  const visibleLineSegments = lineSegments.filter(lineSegment =>
    lineSegment.key !== 'projected-loss-of-control' &&
    lineSegment.key !== 'projected-loss-of-control-triple'
  )
  const futureMarkerDashSegments = showYearDots ? createRenderedFutureMarkerDashSegments(
    lineSources,
    futureMarkerSources,
    xForTimestamp,
    yForValue,
  ) : []
  return [
    ...visibleLineSegments,
    ...futureMarkerDashSegments,
  ]
}

export const createLossOfControlHoverOpacitySource = ({
  showLossOfControlLines,
  backgroundPatternEnabled,
  lossOfControlMinPoints,
  lossOfControlMaxPoints,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
}: {
  showLossOfControlLines: boolean
  backgroundPatternEnabled: boolean
  lossOfControlMinPoints: GraphPoint[]
  lossOfControlMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
}) => {
  if (!showLossOfControlLines || !backgroundPatternEnabled) return null
  const visibleMinPoints = lossOfControlMinPoints.filter(graphPoint =>
    graphPoint.timestamp <= effectiveCappedTimestamp
  )
  const visibleMaxPoints = lossOfControlMaxPoints.filter(graphPoint =>
    graphPoint.timestamp <= effectiveCappedTimestamp
  )
  if (visibleMinPoints.length < 2) return null
  if (visibleMaxPoints.length < 2) return null
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = 0
  let maxY = 0
  const minLineScreenPoints: ScreenPoint[] = []
  for (let pointIndex = 0; pointIndex < visibleMinPoints.length; pointIndex += 1) {
    const boundaryPoint = visibleMinPoints[pointIndex]
    const x = xForTimestamp(boundaryPoint.timestamp)
    const y = yForValue(boundaryPoint.value)
    minLineScreenPoints.push({ x, y })
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  const maxLineScreenPoints: ScreenPoint[] = visibleMaxPoints.map(boundaryPoint => ({
    x: xForTimestamp(boundaryPoint.timestamp),
    y: yForValue(boundaryPoint.value),
  }))
  // Bounds tracks the Min curve's bounding box (with padding) so existing
  // consumers of this source — like the risk-background-label positioner —
  // keep placing labels relative to the visible LoC reference line. Density
  // calls inside `getCurveGradientOpacityAtPoint` use the Min/Max screen
  // points directly, not these bounds.
  const bounds: PatternBounds = {
    x: minX - PATH_BOUNDS_PADDING,
    y: minY - PATH_BOUNDS_PADDING,
    width: (maxX - minX) + PATH_BOUNDS_PADDING * 2,
    height: (maxY - minY) + PATH_BOUNDS_PADDING * 2,
  }
  return { bounds, minLineScreenPoints, maxLineScreenPoints }
}

export const createHoverPoints = ({
  primaryVisibleGraphPoints,
  xForTimestamp,
  yForValue,
  lossOfControlHoverOpacitySource,
  backgroundPatternSettings,
}: {
  primaryVisibleGraphPoints: GraphPoint[]
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  lossOfControlHoverOpacitySource: ReturnType<typeof createLossOfControlHoverOpacitySource>
  backgroundPatternSettings: BackgroundPatternSettings
}): ExtrapolatedGraphHoverPoint[] =>
  primaryVisibleGraphPoints.map(graphPoint => {
    const cx = xForTimestamp(graphPoint.timestamp)
    const cy = yForValue(graphPoint.value)
    const riskOpacity = lossOfControlHoverOpacitySource
      ? getCurveGradientOpacityAtPoint(
        { x: cx, y: cy },
        lossOfControlHoverOpacitySource.minLineScreenPoints,
        lossOfControlHoverOpacitySource.maxLineScreenPoints,
        backgroundPatternSettings
      ) ?? 0
      : 0
    return {
      timestamp: graphPoint.timestamp,
      value: graphPoint.value,
      cx,
      cy,
      riskOpacity,
    }
  })

export const createRiskBackgroundLabels = ({
  redAboveLossOfControl,
  showRiskBackgroundLabels,
  lossOfControlHoverOpacitySource,
  gradientStrength,
  ditherDensity,
  paddingLeft,
  paddingBottom,
  height,
}: {
  redAboveLossOfControl: boolean
  showRiskBackgroundLabels: boolean
  lossOfControlHoverOpacitySource: ReturnType<typeof createLossOfControlHoverOpacitySource>
  gradientStrength: number
  ditherDensity: number
  paddingLeft: number
  paddingBottom: number
  height: number
}): RiskBackgroundLabel[] => {
  const canPositionRiskBackgroundLabels = redAboveLossOfControl
    && lossOfControlHoverOpacitySource
    && gradientStrength > 0
    && ditherDensity > 0
  if (!canPositionRiskBackgroundLabels) return []
  const { bounds, minLineScreenPoints } = lossOfControlHoverOpacitySource
  const leftMinCurvePoint = minLineScreenPoints[0]
  if (!leftMinCurvePoint) return []
  const labelX = Math.max(paddingLeft + 4, leftMinCurvePoint.x + 4)
  const labelAreaTopY = Math.max(8, bounds.y)
  const labelAreaBottomY = Math.min(
    height - paddingBottom,
    bounds.y + bounds.height,
    leftMinCurvePoint.y - 6
  )
  const labelAreaHeight = labelAreaBottomY - labelAreaTopY
  if (labelAreaHeight <= 0) return []
  const riskBackgroundLabelSources = RISK_BACKGROUND_LABELS as RiskBackgroundLabelSource[]
  return riskBackgroundLabelSources.map(riskBackgroundLabel => {
    const labelProgress = riskBackgroundLabel.gradientPercent / 100
    const y = labelAreaBottomY - (labelAreaHeight * labelProgress)
    const guidePathVerticalShift = y - leftMinCurvePoint.y
    const guidePathData = minLineScreenPoints.map((minLineScreenPoint, pointIndex) =>
      `${pointIndex === 0 ? 'M' : 'L'} ${minLineScreenPoint.x} ${minLineScreenPoint.y + guidePathVerticalShift}`
    ).join(' ')
    return {
      ...riskBackgroundLabel,
      x: labelX,
      y,
      guidePathData,
      opacity: showRiskBackgroundLabels ? 1 : 0,
    }
  })
}
